import { getActiveAuth } from '../config/firebase';
import { API_BASE_URL } from '../config/api';

export enum MessageSenderRole {
  OWNER = "owner",
  COLLABORATOR = "collaborator",
  SYSTEM = "system"
}

export interface CollaboratorMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderRole: MessageSenderRole;
  senderName: string;
  content: string;
  createdAt: string;
  readAt?: string;
  metadata?: Record<string, any>;
}

export interface CollaboratorConversation {
  id: string;
  profileId: string;
  profileName?: string;
  participantIds: string[];
  participantNames: Record<string, string>;
  participantRoles: Record<string, string>;
  unreadCounts: Record<string, number>;
  lastMessage?: string;
  lastMessageAt?: string;
  lastSenderId?: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  requiresAcceptance?: boolean;
  initiatorId?: string;
  initiatorName?: string;
  
  // Index-specific fields (from collaborator_conversation_index)
  otherParticipantId?: string;
  otherParticipantName?: string;
  otherParticipantInitials?: string;
  otherParticipantRole?: string;
  unreadCount?: number;
}

class CollaboratorMessagingService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE_URL}/api/v1/collaborator-messaging`;
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const user = getActiveAuth().currentUser;
    if (!user) {
      throw new Error('Authentication required');
    }
    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    };
  }

  /**
   * Start or get a conversation with another collaborator
   */
  async startConversation(
    profileId: string,
    otherUserId: string,
    otherUserName: string,
    otherUserRole: string = 'collaborator'
  ): Promise<CollaboratorConversation> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/conversations/start`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        profile_id: profileId,
        other_user_id: otherUserId,
        other_user_name: otherUserName,
        other_user_role: otherUserRole
      })
    });
    if (!response.ok) throw new Error('Failed to start conversation');
    const data = await response.json();
    return data.conversation;
  }

  /**
   * Create a pending message request (requires recipient acceptance)
   */
  async createMessageRequest(
    profileId: string,
    targetUserId: string,
    targetUserName: string,
    targetUserRole: string = 'collaborator',
    content?: string
  ): Promise<CollaboratorConversation> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/requests/start`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        profile_id: profileId,
        target_user_id: targetUserId,
        target_user_name: targetUserName,
        target_user_role: targetUserRole,
        content
      })
    });
    if (!response.ok) throw new Error('Failed to create message request');
    const data = await response.json();
    return data.conversation;
  }

  /**
   * Accept a pending message request
   */
  async acceptMessageRequest(conversationId: string, profileId: string): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/requests/accept`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        conversation_id: conversationId,
        profile_id: profileId
      })
    });
    if (!response.ok) throw new Error('Failed to accept request');
  }

  /**
   * Send a message in a conversation
   */
  async sendMessage(conversationId: string, profileId: string, content: string): Promise<CollaboratorMessage> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/messages/send`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        conversation_id: conversationId,
        profile_id: profileId,
        content
      })
    });
    if (!response.ok) throw new Error('Failed to send message');
    const data = await response.json();
    return data.message;
  }

  /**
   * Get all conversations for the current user (across all profiles)
   */
  async getInbox(): Promise<CollaboratorConversation[]> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/inbox`, {
      method: 'GET',
      headers
    });
    if (!response.ok) throw new Error('Failed to fetch inbox');
    const data = await response.json();
    return data.conversations;
  }

  /**
   * Get conversations for a specific profile
   */
  async getProfileConversations(profileId: string): Promise<CollaboratorConversation[]> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/inbox/profile/${profileId}`, {
      method: 'GET',
      headers
    });
    if (!response.ok) throw new Error('Failed to fetch conversations');
    const data = await response.json();
    return data.conversations;
  }

  /**
   * Get messages for a conversation
   */
  async getMessages(
    conversationId: string,
    profileId: string,
    limit: number = 50,
    before?: string
  ): Promise<CollaboratorMessage[]> {
    const headers = await this.getAuthHeaders();
    let url = `${this.baseUrl}/conversations/${profileId}/${conversationId}/messages?limit=${limit}`;
    if (before) {
      url += `&before=${before}`;
    }
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    if (!response.ok) throw new Error('Failed to fetch messages');
    const data = await response.json();
    return data.messages;
  }

  /**
   * Mark a conversation as read
   */
  async markAsRead(conversationId: string, profileId: string): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/conversations/read`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        conversation_id: conversationId,
        profile_id: profileId
      })
    });
    if (!response.ok) throw new Error('Failed to mark as read');
  }
}

export const collaboratorMessagingService = new CollaboratorMessagingService();

